import { useState, useEffect, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

interface Slide {
  id: number;
  type: 'video' | 'image';
  src: string;
  caption: string;
}

const ProcessVideoSlideshow = () => {
  const { currentLanguage } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [videoPlayCount, setVideoPlayCount] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const imageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const slides: Slide[] = [
    {
      id: 1,
      type: 'video',
      src: '/lovable-uploads/process-video-2.mp4',
      caption: currentLanguage === 'ro' 
        ? "Arta noastră începe cu alegerea lemnului. Doar cele mai premium esențe, atent selecționate, devin baza produselor Magic Door."
        : "Our art begins with the choice of wood. Only the most premium essences, carefully selected, become the foundation of Magic Door products."
    },
    {
      id: 2,
      type: 'video',
      src: '/lovable-uploads/process-video-1.mp4',
      caption: currentLanguage === 'ro'
        ? "În atelier, precizia și măiestria se împletesc. Fiecare detaliu este lucrat cu grijă, pentru a îmbrățișa viziunea unică a fiecărui client."
        : "In the workshop, precision and craftsmanship intertwine. Every detail is carefully crafted to embrace each client's unique vision."
    },
    {
      id: 3,
      type: 'image',
      src: '/lovable-uploads/process-final-product.webp',
      caption: currentLanguage === 'ro'
        ? "Rezultatul? O piesă născută din inspirația clientului transformată de noi în realitate, cu pasiune. Tu vii cu ideea, noi o punem în practică."
        : "The result? A piece born from the client's inspiration, transformed by us into reality with passion. You bring the idea, we put it into practice."
    }
  ];

  const handleVideoEnded = () => {
    const newCount = videoPlayCount + 1;
    setVideoPlayCount(newCount);

    if (newCount >= 2) {
      // After 2 loops, advance to next slide
      setTimeout(() => {
        advanceSlide();
      }, 1000);
    } else {
      // Replay video
      if (videoRef.current) {
        videoRef.current.play();
      }
    }
  };

  const advanceSlide = () => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setVideoPlayCount(0);
    
    setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
      setIsTransitioning(false);
    }, 300);
  };

  const handleManualNavigation = (index: number) => {
    if (isTransitioning || index === currentSlide) return;
    
    setIsTransitioning(true);
    setVideoPlayCount(0);
    
    // Clear any existing timers
    if (imageTimerRef.current) {
      clearTimeout(imageTimerRef.current);
      imageTimerRef.current = null;
    }
    
    setTimeout(() => {
      setCurrentSlide(index);
      setIsTransitioning(false);
    }, 300);
  };

  // Handle image slide auto-advance
  useEffect(() => {
    if (slides[currentSlide].type === 'image' && !isTransitioning) {
      imageTimerRef.current = setTimeout(() => {
        advanceSlide();
      }, 8000); // 8 seconds for image
    }

    return () => {
      if (imageTimerRef.current) {
        clearTimeout(imageTimerRef.current);
        imageTimerRef.current = null;
      }
    };
  }, [currentSlide, isTransitioning]);

  // Auto-play video when slide changes
  useEffect(() => {
    if (slides[currentSlide].type === 'video' && videoRef.current && !isTransitioning) {
      videoRef.current.play().catch(err => console.log('Video autoplay prevented:', err));
    }
  }, [currentSlide, isTransitioning]);

  const currentSlideData = slides[currentSlide];

  return (
    <section className="py-20 px-4 container mx-auto">
      {/* Title */}
      <h2 className="text-4xl md:text-5xl lg:text-6xl font-playfair text-center mb-12 text-foreground animate-fade-in">
        De Ce MagicDoor
        <span className="block text-2xl md:text-3xl lg:text-4xl mt-2 text-muted-foreground">
          (Procesul Nostru)
        </span>
      </h2>

      {/* Video/Image Container */}
      <div className="relative max-w-5xl mx-auto">
        {/* Glass Container */}
        <div className="relative rounded-3xl overflow-hidden backdrop-blur-3xl bg-white/50 dark:bg-white/20 border-2 border-white/40 shadow-[0_0_40px_rgba(0,0,0,0.15),0_0_80px_rgba(0,0,0,0.1)] transition-all duration-700">
          {/* Aspect Ratio Container */}
          <div className="relative aspect-video">
            {slides.map((slide, index) => (
              <div
                key={slide.id}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                }`}
              >
                {slide.type === 'video' ? (
                  <video
                    ref={index === currentSlide ? videoRef : null}
                    src={slide.src}
                    className="w-full h-full object-cover"
                    muted
                    playsInline
                    preload={index === currentSlide ? "auto" : "metadata"}
                    onEnded={handleVideoEnded}
                    aria-label={`Process video ${slide.id} of 3`}
                  />
                ) : (
                  <img
                    src={slide.src}
                    alt="Final product showcase"
                    className="w-full h-full object-cover"
                  />
                )}

                {/* Desktop Navigation - Top Left on Video/Image */}
                <div className="hidden md:flex absolute top-6 left-6 gap-3 z-20">
                  {slides.map((s, idx) => (
                    <button
                      key={s.id}
                      onClick={() => handleManualNavigation(idx)}
                      disabled={isTransitioning}
                      className={`
                        backdrop-blur-md transition-all duration-500 rounded-lg px-4 py-2
                        ${
                          idx === currentSlide
                            ? 'bg-white/80 border-2 border-white/90 shadow-[0_0_20px_rgba(255,255,255,0.5)] scale-105 text-foreground'
                            : 'bg-white/20 border-2 border-white/30 hover:bg-white/40 hover:scale-105 text-white/80'
                        }
                        disabled:cursor-not-allowed font-light tracking-wider
                      `}
                      aria-label={`Go to slide ${idx + 1} of 3`}
                    >
                      0{s.id}
                    </button>
                  ))}
                </div>

                {/* Caption Overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6 md:p-8">
                  <p className="text-white text-lg md:text-xl lg:text-2xl font-light leading-relaxed animate-fade-in">
                    {slide.caption}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Navigation - Bottom Indicators */}
        <div className="flex md:hidden justify-center gap-4 mt-8">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              onClick={() => handleManualNavigation(index)}
              disabled={isTransitioning}
              className={`
                backdrop-blur-md transition-all duration-500 rounded-lg px-4 py-2
                ${
                  index === currentSlide
                    ? 'bg-white/70 border-2 border-white/80 shadow-[0_0_20px_rgba(255,255,255,0.4)] scale-105 text-foreground'
                    : 'bg-white/30 border-2 border-white/40 hover:bg-white/50 hover:scale-105 text-foreground/70'
                }
                disabled:cursor-not-allowed font-light tracking-wider
              `}
              aria-label={`Go to slide ${index + 1} of 3`}
              aria-current={index === currentSlide ? 'true' : 'false'}
            >
              0{slide.id}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessVideoSlideshow;
